import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAngularModule } from '../../../modules/mat-angular.module';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { BrandIndexService, Plan, Role } from '../../../../services/brand-index.service';
import { EducationConfigService, EducationConfigResponse } from '../../../../services/education-config.service';
import { DynamicUniversitySearchComponent, SelectedUniversity } from '../../dynamic-university-search/dynamic-university-search.component';
import { HorizontalSelectorBarComponent, SelectorItem } from '../../horizontal-selector-bar/horizontal-selector-bar.component';
import { TemplateCommonService } from '../services/template-common.service';
import { CommonService } from '../../../../services/common.service';

export interface FilterConfig {
  entityType: 'university' | 'brand';
  entityLabel: string;
  entityPlural: string;
  categoryLabel: string;
  categoryOptions: string[];
  countryLabel: string;
  countryOptions: string[];
  searchPlaceholder: string;
  buttonText: string;
}

export interface FilterData {
  category: string;
  country: string;
  selectedEntities: SelectedUniversity[];
}

@Component({
  selector: 'app-common-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAngularModule,
    MatProgressBarModule,
    MatCardModule,
    MatTableModule,
    MatAutocompleteModule,
    MatChipsModule,
    HorizontalSelectorBarComponent
  ],
  templateUrl: './common-filter.component.html',
  styleUrl: './common-filter.component.scss'
})
export class CommonFilterComponent implements OnInit {
  @Input() config!: FilterConfig;
  @Input() role: Role = 'student';
  @Input() plan: Plan = 'free';
  @Input() error: string | null = null;
  @Input() preselectedUniversity: string | null = null;

  @Output() filterChange = new EventEmitter<FilterData>();
  @Output() analyzeClick = new EventEmitter<FilterData>();

  // Filter values
  category = 'MBA';
  country = 'Global';
  selectedEntities: SelectedUniversity[] = [];

  // Horizontal selector bar data
  selectorItems: SelectorItem[] = [];
  availableOptions: string[] = [];

  // Available options
  categoryOptions: string[] = [];
  countryOptions: string[] = [];
  models: string[] = ['chatgpt'];

  constructor(
    private api: BrandIndexService,
    private educationConfigService: EducationConfigService,
    private templateCommonService: TemplateCommonService,
    protected commonService: CommonService,
  ) { }

  ngOnInit(): void {
    this.loadInitialData();

    // Handle preselected university if provided
    if (this.preselectedUniversity) {
      this.handlePreselectedUniversity();
    }
  }

  private loadInitialData(): void {
    // Load config based on entity type
    if (this.config.entityType === 'university') {
      this.loadEducationConfig();
    } else {
      this.loadBrandConfig();
    }
  }

  private loadEducationConfig(): void {
    this.educationConfigService.getEducationConfig().subscribe({
      next: (cfg: EducationConfigResponse) => {
        this.categoryOptions = cfg.courses || [];
        this.countryOptions = (cfg.countries || []);
        if (!this.countryOptions.includes('Global')) {
          this.countryOptions = ['Global', ...this.countryOptions];
        }
        this.models = (cfg.default_models && cfg.default_models.length) ? cfg.default_models : ['chatgpt'];

        // Set default category if available and not already set
        if (this.categoryOptions.length > 0 && !this.category) {
          this.category = this.categoryOptions[0];
        }

        // Initialize horizontal selector bar with sample universities
        this.initializeHorizontalSelector();
      },
      error: () => {
        this.error = 'Failed to load config';
        // Initialize with fallback data
        this.initializeHorizontalSelector();
      }
    });
  }

  private loadBrandConfig(): void {
    // For brands, we'll use the provided config options
    this.categoryOptions = this.config.categoryOptions || [];
    this.countryOptions = this.config.countryOptions || [];
    if (!this.countryOptions.includes('Global')) {
      this.countryOptions = ['Global', ...this.countryOptions];
    }

    // Set default category if available and not already set
    if (this.categoryOptions.length > 0 && !this.category) {
      this.category = this.categoryOptions[0];
    }

    // Initialize horizontal selector bar
    this.initializeHorizontalSelector();
  }

  private initializeHorizontalSelector(): void {
    if (this.config.entityType === 'university') {
      // Load universities from backend instead of using hardcoded samples
      this.loadUniversitiesFromBackend();
    } else if (this.config.entityType === 'brand') {
      // Sample brands for the horizontal selector
      this.availableOptions = [
        'Apple',
        'Google',
        'Microsoft',
        'Amazon',
        'Tesla',
        'Nike',
        'Coca-Cola',
        'McDonald\'s',
        'Starbucks',
        'Disney',
        'Netflix',
        'Facebook',
        'Twitter',
        'Uber',
        'Airbnb'
      ];

      // Initialize with default "You" selection
      this.selectorItems = [
        {
          id: 'primary',
          name: 'Select your brand',
          type: 'primary',
          color: '#2196F3',
          removable: false
        }
      ];
    }
  }

  private loadUniversitiesFromBackend(): void {
    if (!this.category || !this.country) {
      // // console.log('Category or country not set, using fallback universities');
      this.availableOptions = [
        'Imperial College London',
        'University of Oxford',
        'University of Cambridge',
        'London Business School',
        'University of Manchester',
        'University College London',
        'King\'s College London',
        'University of Edinburgh',
        'University of Bristol',
        'University of Warwick',
        'University of Birmingham',
        'University of Leeds',
        'University of Sheffield',
        'University of Nottingham',
        'University of Southampton'
      ];
      // Initialize selector items (will handle preselection if available)
      this.initializeSelectorItems();
      return;
    }

    // // console.log(`Loading universities for ${this.country} and ${this.category}`);

    // Convert 'Global' to 'UK' for API calls
    const countryParam = this.country === 'Global' ? 'UK' : this.country;

    this.api.getAvailableUniversities(countryParam, this.category).subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data && Array.isArray(response.data)) {
          // Extract university names from the response
          this.availableOptions = response.data.map((uni: any) => uni.name || uni);
          // // console.log(`Loaded ${this.availableOptions.length} universities from backend`);
        } else if (response.universities && Array.isArray(response.universities)) {
          // Alternative response format
          this.availableOptions = response.universities.map((uni: any) => uni.name || uni);
          // // console.log(`Loaded ${this.availableOptions.length} universities from backend (alt format)`);
        } else {
          console.warn('Unexpected response format, using fallback universities');
          this.availableOptions = [
            'Imperial College London',
            'University of Oxford',
            'University of Cambridge',
            'London Business School',
            'University of Manchester'
          ];
        }
        // Initialize selector items (will handle preselection if available)
        this.initializeSelectorItems();
      },
      error: (error: any) => {
        console.error('Error loading universities from backend:', error);
        // Use fallback universities
        this.availableOptions = [
          'Imperial College London',
          'University of Oxford',
          'University of Cambridge',
          'London Business School',
          'University of Manchester',
          'University College London',
          'King\'s College London',
          'University of Edinburgh',
          'University of Bristol',
          'University of Warwick',
          'University of Birmingham',
          'University of Leeds',
          'University of Sheffield',
          'University of Nottingham',
          'University of Southampton'
        ];
        // Initialize selector items (will handle preselection if available)
        this.initializeSelectorItems();
      }
    });
  }


  onCategoryChange(): void {
    // // console.log(`${this.config.categoryLabel} changed to:`, this.category);
    this.selectedEntities = [];
    // Reset compare state when category changes
    this.templateCommonService.setCompareClicked(false);
    if (this.config.entityType === 'university') {
      this.loadUniversitiesFromBackend();
    }
    this.emitFilterChange();
  }

  onCountryChange(): void {
    // // console.log(`${this.config.countryLabel} changed to:`, this.country);
    this.selectedEntities = [];
    // Reset compare state when country changes
    this.templateCommonService.setCompareClicked(false);
    if (this.config.entityType === 'university') {
      this.loadUniversitiesFromBackend();
    }
    this.emitFilterChange();
  }

  onEntitiesChange(entities: SelectedUniversity[]): void {
    this.selectedEntities = entities;
    // // console.log(`${this.config.entityPlural} changed:`, entities);
    // // console.log('Button should be enabled:', entities.length > 0);
    // Reset compare state when entities change
    this.templateCommonService.setCompareClicked(false);
    this.emitFilterChange();
  }

  onSelectorSelectionChange(items: SelectorItem[]): void {
    this.selectorItems = items;
    // Convert SelectorItem[] to SelectedUniversity[]
    const defaultName = this.config.entityType === 'university' ? 'Select your university' : 'Select your brand';

    // Filter out default names and duplicates
    const uniqueItems = items
      .filter(item => item.name && item.name !== defaultName)
      .filter((item, index, self) =>
        index === self.findIndex(t => t.name === item.name)
      );

    this.selectedEntities = uniqueItems.map(item => ({
      id: parseInt(item.id.replace('competitor-', '')) || 1,
      name: item.name,
      category: this.category,
      country: this.country
    }));

    // // console.log('Horizontal selector changed:', items);
    // // console.log('Converted entities (deduplicated):', this.selectedEntities);
    this.emitFilterChange();
  }

  onSelectorCompareClick(items: SelectorItem[]): void {
    // // console.log('Compare clicked with items:', items);
    // Set the compare state to true when COMPARE button is clicked
    this.templateCommonService.setCompareClicked(true);
    this.onAnalyzeClick();
  }

  onAnalyzeClick(): void {
    if (this.isAnalyzeButtonEnabled()) {
      this.analyzeClick.emit({
        category: this.category,
        country: this.country,
        selectedEntities: this.selectedEntities
      });
    }
  }

  isAnalyzeButtonEnabled(): boolean {
    return this.selectedEntities.length > 0;
  }

  private emitFilterChange(): void {
    this.filterChange.emit({
      category: this.category,
      country: this.country,
      selectedEntities: this.selectedEntities
    });
  }

  private handlePreselectedUniversity(): void {
    if (!this.preselectedUniversity) return;

    // console.log('Handling preselected university:', this.preselectedUniversity);

    // Create a selected university object for the preselected university
    const preselectedEntity: SelectedUniversity = {
      id: 1, // Use a default ID
      name: this.preselectedUniversity,
      category: this.category,
      country: this.country
    };

    // Add the preselected university to selected entities
    this.selectedEntities = [preselectedEntity];

    // Update the selector items to show the preselected university with "YOU" label
    this.selectorItems = [
      {
        id: 'primary',
        name: `${this.preselectedUniversity} (YOU)`,
        type: 'primary',
        color: '#4CAF50', // Green color to indicate "YOU"
        removable: false
      }
    ];

    // Emit the filter change to update the parent component
    this.emitFilterChange();

    // console.log('Preselected university set:', this.selectedEntities);
  }

  // Override the initializeSelectorItems method to handle preselection
  private initializeSelectorItems(): void {
    // If we have a preselected university, use it instead of the default
    if (this.preselectedUniversity) {
      this.handlePreselectedUniversity();
      return;
    }

    // // console.log('initializeSelectorItems - selectorItems.length:', this.selectorItems.length);
    // // console.log('initializeSelectorItems - current selectorItems:', this.selectorItems);
    // Only initialize if no items are selected yet
    if (this.selectorItems.length === 0) {
      // // console.log('Initializing selector items with default');
      // Initialize with default "You" selection
      this.selectorItems = [
        {
          id: 'primary',
          name: 'Select your university',
          type: 'primary',
          color: '#2196F3',
          removable: false
        }
      ];
      // // console.log('Selector items initialized:', this.selectorItems);
    } else {
      // // console.log('Skipping initialization - items already exist');
    }
  }
}
